import {
  type DaysFixed,
  DEFAULT_PROTOCOL,
  findProtocol,
  type PlanOutput,
  type PlanSplit,
  PREFERRED_SPLITS,
  type PreferredSplit,
  type ProtocolSplit,
  SPLIT_MIN_DAYS,
  type TrainingConfigInput,
  type TrainingConfigOutput,
} from "@repo/contract/training";

/**
 * The routine editor's pure half, ported from A2 `profile/routine.tsx`
 * (`splitFor` :25-31, `fillDays` :279-290, the `sameOf` / step-1 logic
 * :103-160). T24 did not move them to `@repo/client/format`, so they live
 * here, beside their only callers.
 *
 * ── WEEKDAYS ARE `0` = SUNDAY HERE ──────────────────────────────────────────
 * A2 indexed its week from Monday. The contract's `fixedWeekdays` is
 * `Date#getDay` numbering (`0` Sunday), as is `@repo/client/format`'s
 * `weekdayShort`, so every A2 index was converted (`(i + 1) % 7`): A2's
 * preferred order Mon, Wed, Fri, Sun, Tue, Thu, Sat is `[1, 3, 5, 0, 2, 4, 6]`.
 * The week is DRAWN Sunday first ({@link DISPLAY_WEEK}, `D218`), as a
 * Brazilian calendar is, and its workouts are still LETTERED Monday first
 * ({@link LETTER_WEEK}): drawing changed, what the preview promises did not.
 */

/** Sunday first — the order every week picker and strip is drawn in. */
export const DISPLAY_WEEK: readonly number[] = [0, 1, 2, 3, 4, 5, 6];

/**
 * Monday first — the order a fixed week's days take their workout letters
 * in, unchanged by `D218`'s Sunday-first drawing: Monday, Wednesday and
 * Sunday are still A, B and C.
 */
export const LETTER_WEEK: readonly number[] = [1, 2, 3, 4, 5, 6, 0];

/** The contract's bounds on `daysPerWeek` (training-config.ts). A2 allowed
 *  one day; the engine does not. */
export const MIN_DAYS = 2;
export const MAX_DAYS = 6;

const LETTERS = ["A", "B", "C", "D", "E", "F"] as const;

export type SplitKind =
  | "arms"
  | "full_body"
  | "legs"
  | "lower"
  | "pull"
  | "push"
  | "upper";

/**
 * Every shape a week can be drawn as: a protocol's split (`by_days` derived
 * from the day count), one the person or the trainer chose (`D213`, `D216`),
 * or a custom week the trainer wrote day by day.
 */
export type WeekShape = ProtocolSplit | PlanSplit;

/**
 * Each split's day rotation, as the engine's `weekDays` walks it:
 * upper/lower alternates from upper, PPL cycles push, pull, legs, and
 * PPL + arms adds the arms day. The engine letters a repeated day ("Upper
 * A"); the preview shows only the kind.
 */
const ROTATIONS: Readonly<Record<PreferredSplit, readonly SplitKind[]>> = {
  full_body: ["full_body"],
  ppl: ["push", "pull", "legs"],
  ppl_arms: ["push", "pull", "legs", "arms"],
  upper_lower: ["upper", "lower"],
};

/** `by_days` at a day count: two or three full body, four upper/lower, five
 *  and six PPL (the rule `intake.why.days_per_week` states). */
export function derivedSplit(total: number): PreferredSplit {
  if (total <= 3) {
    return "full_body";
  }
  return total === 4 ? "upper_lower" : "ppl";
}

/**
 * The engine's `resolveSplit`, plus the server's fallback (`D216`): a split
 * the week has too few days for is never built short — the generator falls
 * back to the protocol's — so the preview falls back to `by_days` too. A
 * protocol that names its split keeps it at any day count: both
 * intermediate protocols build upper/lower at five and six days.
 */
function resolveSplit(
  split: Exclude<WeekShape, "custom">,
  total: number
): PreferredSplit {
  if (split === "by_days" || total < SPLIT_MIN_DAYS[split]) {
    return derivedSplit(total);
  }
  return split;
}

export interface SplitDay {
  /** `null` for a custom day, which is named by its own label. */
  readonly kind: SplitKind | null;
  readonly letter: string;
  /** A custom day's label, as the trainer wrote it. */
  readonly name?: string;
}

/**
 * The week the engine builds for `total` days as `shape`. Empty outside the
 * engine's two to six days, which it refuses to build. A custom week is
 * drawn from its own days (`custom`) while their count is `total`; at
 * another count the generator would not keep it (`incumbentWeek`), and the
 * preview shows the day count's derived week instead.
 */
export function splitFor(
  total: number,
  shape: WeekShape,
  custom: readonly string[] = []
): SplitDay[] {
  if (!Number.isInteger(total) || total < MIN_DAYS || total > MAX_DAYS) {
    return [];
  }
  if (shape === "custom" && custom.length === total) {
    return custom.map((name, i) => ({
      kind: null,
      letter: LETTERS[i] ?? "",
      name,
    }));
  }
  const rotation =
    ROTATIONS[resolveSplit(shape === "custom" ? "by_days" : shape, total)];
  return Array.from({ length: total }, (_, i) => ({
    kind: rotation[i % rotation.length] as SplitKind,
    letter: LETTERS[i] ?? "",
  }));
}

/**
 * The shape of the plan in effect: the split the server says it was BUILT as
 * (`planView.split`, `D213`) — a trainer's PPL + arms or custom week shows
 * as itself — else its protocol's, else the default protocol's when there is
 * no plan yet or the plan has no protocol (written by hand).
 */
export function planSplit(
  plan: Pick<NonNullable<PlanOutput>, "protocolId" | "split"> | null | undefined
): WeekShape {
  const built = plan?.split ?? null;
  if (built !== null) {
    return built;
  }
  const id = plan?.protocolId ?? null;
  const protocol = id === null ? undefined : findProtocol(id);
  return protocol?.split ?? DEFAULT_PROTOCOL.split;
}

/** The splits a week of `total` days can be built as, in the engine's order. */
export function splitChoices(total: number): readonly PreferredSplit[] {
  return PREFERRED_SPLITS.filter((split) => total >= SPLIT_MIN_DAYS[split]);
}

/**
 * The week the draft would be drawn as: the chosen split, else the plan's
 * own shape — exactly what the generator builds when nothing is chosen
 * (`D216`'s order, minus the incumbent test only the server can make).
 */
export function previewShape(
  chosen: PreferredSplit | null,
  plan: WeekShape
): WeekShape {
  return chosen ?? plan;
}

/** A2's preferred weekdays, converted to `0` = Sunday (see the header). */
const PREFERRED: readonly number[] = [1, 3, 5, 0, 2, 4, 6];

/**
 * Adds the earliest free preferred weekdays, spaced by one where possible,
 * until there are `count` (never more than {@link MAX_DAYS}). Ascending.
 */
export function fillDays(days: readonly number[], count: number): number[] {
  const out = [...new Set(days)];
  for (const preferred of PREFERRED) {
    if (out.length >= Math.min(count, MAX_DAYS)) {
      break;
    }
    if (!out.includes(preferred)) {
      out.push(preferred);
    }
  }
  return out.sort((a, b) => a - b);
}

/** A2's three answers to "Esses dias são sempre os mesmos?". */
export type Same = "all" | "none" | "some";

/** The editor's draft: which days are fixed, how many float, and the split. */
export interface RoutineForm {
  readonly fixed: readonly number[];
  readonly flex: number;
  readonly same: Same;
  /** The week's shape the person chose, `null` for the engine's (`D216`). */
  readonly split: PreferredSplit | null;
}

const SAME_OF: Readonly<Record<DaysFixed, Same>> = {
  fixed: "all",
  mixed: "some",
  variable: "none",
};

const DAYS_FIXED_OF: Readonly<Record<Same, DaysFixed>> = {
  all: "fixed",
  some: "mixed",
  none: "variable",
};

type RoutineFields = Pick<
  TrainingConfigOutput,
  "daysFixed" | "daysPerWeek" | "fixedWeekdays" | "preferredSplit"
>;

/**
 * Whether the server knows `preferredSplit` (`D216`): it echoes the field —
 * `null` included — once it does. An older server never sees the key, which
 * its strict input would answer 422 (`D172`), so the editor neither offers
 * the choice nor sends it until then.
 */
export function splitKnown(config: RoutineFields | null): boolean {
  return config?.preferredSplit !== undefined;
}

/** The draft a stored config opens as. No config yet: three floating days. */
export function routineFormFrom(config: RoutineFields | null): RoutineForm {
  if (config === null) {
    return { fixed: [], flex: 3, same: "none", split: null };
  }
  const split = config.preferredSplit ?? null;
  const fixed = [...new Set(config.fixedWeekdays)].sort((a, b) => a - b);
  const same = SAME_OF[config.daysFixed];
  if (same === "none") {
    return { fixed: [], flex: config.daysPerWeek, same, split };
  }
  const flex = Math.max(0, config.daysPerWeek - fixed.length);
  // A "fixed" week the server stored with fewer named days than its count is
  // read as it behaves: some fixed, some floating.
  return {
    fixed,
    flex,
    same: same === "all" && flex > 0 ? "some" : same,
    split,
  };
}

/**
 * The draft with a split the week no longer fits cleared — PPL chosen and
 * the days then lowered to two. The contract refuses that pair, and the
 * server clears it the same way when only the days change.
 */
export function fitSplit(form: RoutineForm): RoutineForm {
  const { total } = resolveRoutine(form);
  return form.split !== null && total < SPLIT_MIN_DAYS[form.split]
    ? { ...form, split: null }
    : form;
}

/**
 * What the answers MEAN, whatever the toggles still hold from another mode
 * (A2 routine.tsx:125-128): "none" names no day, "all" floats none.
 */
export function resolveRoutine(form: RoutineForm): {
  readonly fixed: readonly number[];
  readonly flex: number;
  readonly total: number;
} {
  const fixed =
    form.same === "none" ? [] : [...new Set(form.fixed)].sort((a, b) => a - b);
  const flex = form.same === "all" ? 0 : form.flex;
  return { fixed, flex, total: fixed.length + flex };
}

export type RoutineIssue = "some_needs_both" | "too_few" | "too_many";

/** Why the draft cannot be applied yet, or `null`. */
export function routineIssue(form: RoutineForm): RoutineIssue | null {
  const { fixed, flex, total } = resolveRoutine(form);
  if (total > MAX_DAYS) {
    return "too_many";
  }
  if (form.same === "some" && (fixed.length === 0 || flex === 0)) {
    return "some_needs_both";
  }
  if (total < MIN_DAYS) {
    return "too_few";
  }
  return null;
}

/**
 * The step-1 "Continuar" (A2 routine.tsx:143-153): moving to "all" gives the
 * floating days a weekday; moving to "none" turns the fixed days into a count.
 */
export function switchSame(form: RoutineForm, same: Same): RoutineForm {
  if (same === "all" && form.flex > 0) {
    return {
      ...form,
      fixed: fillDays(form.fixed, form.fixed.length + form.flex),
      flex: 0,
      same,
    };
  }
  if (same === "none" && form.fixed.length > 0) {
    return { ...form, fixed: [], flex: form.flex + form.fixed.length, same };
  }
  if (same === "some" && form.flex === 0) {
    return { ...form, flex: 1, same };
  }
  return { ...form, same };
}

export function toggleDay(form: RoutineForm, day: number): RoutineForm {
  const fixed = form.fixed.includes(day)
    ? form.fixed.filter((d) => d !== day)
    : [...form.fixed, day];
  return { ...form, fixed };
}

/**
 * The training-config fields the routine editor owns: the three about days,
 * and the split when the server knows it ({@link splitKnown}).
 */
export function routinePatch(
  form: RoutineForm,
  config: RoutineFields | null
): Pick<
  TrainingConfigInput,
  "daysFixed" | "daysPerWeek" | "fixedWeekdays" | "preferredSplit"
> {
  const { fixed, total } = resolveRoutine(form);
  const days = {
    daysFixed: DAYS_FIXED_OF[form.same],
    daysPerWeek: total,
    fixedWeekdays: [...fixed],
  };
  return splitKnown(config) ? { ...days, preferredSplit: form.split } : days;
}

/** Whether applying the draft would change nothing stored. */
export function routineUnchanged(
  form: RoutineForm,
  config: RoutineFields | null
): boolean {
  if (config === null) {
    return false;
  }
  const next = routinePatch(form, config);
  const stored = [...config.fixedWeekdays].sort((a, b) => a - b);
  return (
    next.daysFixed === config.daysFixed &&
    next.daysPerWeek === config.daysPerWeek &&
    next.fixedWeekdays.join() === stored.join() &&
    (next.preferredSplit ?? null) === (config.preferredSplit ?? null)
  );
}
